import React, { useMemo, useState } from 'react';
import MiscUtils from 'services/MiscUtils';
import { FILE_CMD } from 'hooks/useChat';
import DocumentUtils from 'services/DocumentUtils';
import DocumentPreview from 'components/Utils/DocumentPreview';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import Icon from 'components/Utils/NewIcon';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import { downloadBlob } from 'services/FileDownloadUtils';

const MediaChatMessage = ({ message, patient, roomName }) => {
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) => state.authentication.role);

  const [savingDocument, setSavingDocument] = useState();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const name = (message.text || message.message).replace(FILE_CMD, '').trim();

  async function onSaveDocument(document) {
    setSavingDocument(true);
    let blob = await MiscUtils.urlToBlob(data.url);
    let file = new File([blob], name, {
      type: data.type,
    });
    const { title, document_type, patient_id } = document;
    let result;
    if (patient_id) {
      result = await dispatch(
        api.Documents.addDocument({
          id: patient_id,
          file,
          documentType: document_type,
          title,
          approved: false,
        }),
        { errorTitle: 'Save Attachment' },
      );
    } else {
      result = await dispatch(api.Documents.addUnassignedDocument({ file }), {
        errorTitle: 'Save Attachment',
      });
    }

    if (result)
      result = await dispatch(
        api.Documents.updateDocument({ patientDocument: { ...result, ...document } }),
        {
          errorTitle: 'Save Attachment',
        },
      );

    if (result) {
      setData();
      setSavingDocument(undefined);
      return result;
    }
  }

  async function onClick() {
    setLoading(true);

    if (userRole === 'patient') {
      await downloadBlob(
        api.Chat.getChatFile,
        { name, chatId: roomName },
        { filename: name, timeout: 20_000 },
      );
    } else {
      let result = await dispatch(api.Chat.getChatFile({ name, chatId: roomName }), {
        errorTitle: 'Attachment Preview',
      });
      if (result) {
        let type = result.type;
        let url = MiscUtils.getBlobUrl(result);
        setData({ type, url });
      }
    }
    setLoading(false);
  }

  const defaultDocument = useMemo(() => {
    return {
      patient_id: patient?.id,
      title: name,
      document_type: DocumentUtils.DOCUMENT_TYPE.other,
      from: 'Patient Chat',
      date: message.time,
      direction: 'Inbound',
    };
  }, [name, patient, message.time]);

  return (
    <div className="media-chat-message">
      <div className="message-attachments">
        {!loading && (
          <>
            {!data ? (
              <div className="icon-wrapper">
                <Icon
                  onClick={onClick}
                  fontAwesome="paperclip"
                  alt={name}
                  showTooltip={true}
                  width="15px"
                  height="15px"
                />
              </div>
            ) : (
              <>
                {userRole === 'patient' && (
                  <DefaultPatientModal
                    className="modal add-card-modal"
                    key="event-details-modal"
                    wrap={true}
                    fullHeight={true}
                    padding={false}
                    scroll={false}
                    title="Chat Attachment"
                    content={<DocumentPreview url={data?.url} type={data?.type} />}
                    isOpen={!!data}
                    onRequestClose={() => setData()}
                  />
                )}
                {userRole !== 'patient' && (
                  <DocumentDetailsModal
                    title="Chat Attachment"
                    isOpen={!!data}
                    document={defaultDocument}
                    onClose={() => setData()}
                    disableEdit={userRole === 'patient'}
                    hideEditDocument={true}
                    allowLink={true}
                    disableSend={true}
                    allowDelete={false}
                    allowExpand={false}
                    allowEdit={false}
                    loading={savingDocument}
                    onSave={onSaveDocument}
                    previewElement={
                      <div className="large-resource-preview">
                        <DocumentPreview url={data?.url} type={data?.type} />
                      </div>
                    }
                  />
                )}
              </>
            )}
          </>
        )}
        <ProgressMessage size={15} show={loading} />
      </div>
    </div>
  );
};

export default MediaChatMessage;
